import {Fragment, useContext, useState} from "react";
import classNames from "classnames";
import {PRODUCT_TEASER_IMAGE_STYLE} from "@/types/image-styles";
import {MediaImage} from "@/components/templates/media/media--image";
import VariationSelect from "../../organisms/widget--variation-select";
import {NodeProductDocumentButton} from "@/components/molecules/product-document-button";
import {Button} from "@/components/atoms/button";
import {Link} from "@/components/atoms/link";
import {useRouter} from "next/router";
import {getProductVariations} from "@/lib/get-product-variations";
import parse from "html-react-parser";
import {DrupalTaxonomyTerm} from "next-drupal";
import {Dictionary, DrupalProductVariationGroup} from "@/types/hygiena-types";
import {DictionaryContext} from "@/context/dictionary-context";
import {getDocumentUri} from "@/lib/get-document-uri";
import RenderProductNumber from "@/components/molecules/product-numbers";

/**
 * The teaser display for products.
 *
 * @param node
 * @param props
 * @constructor
 */
export function NodeProductTeaser({node, ...props}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {locale, defaultLocale} = useRouter();
  const productBrochure = node?.field_product_brochure;
  const productManual = node?.field_product_product_manual;
  const productSheet = node?.field_product_product_sheet;
  const readyReferenceSheet = node?.field_product_quick_guide;
  const variations = getProductVariations(node);
  const [activeVariant, setActiveVariant] = useState<DrupalProductVariationGroup|null>(variations?.[0] ?? null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [buttonOpen, setButtonOpen] = useState<boolean>(false);
  const certificates = node?.field_product_certificates?.length
    ? node.field_product_certificates.filter(item => item?.field_certificate_type?.name !== undefined)
    : [];
  const certsNoFile = node?.field_product_certs_no_file?.filter(certificate => certificate?.name?.length) ?? [];

  /**
   * Returns either the term name or display title.
   *
   * @param term
   */
  function getTestTargetDisplay(term: DrupalTaxonomyTerm) {
    if (term?.field_test_target_display_title?.value) return <>{parse(term.field_test_target_display_title.value)}</>;
    return <>{term.name}</>;
  }

  return (
    <article className={classNames(
      "product--teaser bg-white py-8 px-6 drop-shadow-md flex flex-col h-auto justify-between rounded-md", {
        "z-0": !buttonOpen,
        "z-10": buttonOpen
      },
    )}>
      <div className="product-teaser--content flex flex-col h-auto">
        {node?.field_product_image?.length && (
          <div className="product-teaser---image text-center font-semibold mx-auto mb-4">
            <MediaImage
              media={node.field_product_image[0]}
              key={`node-product-teaser--${node.field_product_image[0].id}`}
              imageStyle={PRODUCT_TEASER_IMAGE_STYLE}
              loading="lazy"
              className="h-52 flex"
              sizes="(min-width: 968px) 250px, (min-width: 768px) 200px, 50vw"
              style={{objectFit: "contain", maxWidth: "100%", margin: "auto", maxHeight: "100%"}}
            />
          </div>
        )}
        <div className="product-teaser--title min-h-[70px] text-black flex-grow">
          {node?.field_product_brand?.name && (
            <div
              className="text-center font-semibold text-[20px] md:text-2xl">{node.field_product_brand.name}{node.field_product_brand?.field_brand_suffix ?? ''}</div>
          )}
          <div className="text-center font-semibold mb-4 text-[20px] md:text-2xl">
            {node?.field_product_display_title?.value ? parse(node?.field_product_display_title.value, {}) : node.title}
          </div>
        </div>
        {node?.body?.processed ? (
          <div className="product-teaser--body pt-2 body [&>p]:text-base">
            {parse(node.body.processed, {})}
          </div>
        ) : <></>}
        <div className="product-teaser--attributes pt-2">
          {node?.field_product_test_targets?.length ?
            (
              <div className="product-teaser--detection text-left">{t?.all?.detection}: {
                (() => {
                  return node.field_product_test_targets.map((term, idx) => (
                    <Fragment
                      key={`test-target--${term.id}--${idx}`}>{getTestTargetDisplay(term)}{idx < node.field_product_test_targets.length - 1 ? ", " : ""}</Fragment>
                  ));
                })()
              }</div>
            ) : (<></>)
          }
          {!node?.field_product_class && (certificates?.length || certsNoFile?.length) ? (
            <div
              className="product-teaser--certificates">{certificates?.length > 1 ? `${t?.all?.certificates}` : `${t?.all?.certificate}`}:&nbsp;
              {certificates?.length ? certificates.map((item, i) => (
                <Link key={`node-product-teaser--certificates--${item.id}-${i}`} href={getDocumentUri(item) ?? "/"}
                      target={"_blank"} className="text-primary hover:underline">
                  {item?.field_certificate_type?.name}{item?.field_document_language?.[0]?.abbv && item?.field_document_language?.[0]?.abbv !== 'EN' ? ` (${item?.field_document_language[0].abbv})` : ''}
                  {(i !== certificates.length - 1 || certsNoFile?.length) ? ", " : ""}
                </Link>
              )) : ""}
              {certsNoFile?.length ? certsNoFile.map((item, i) => (
                <span key={`certificates-with-no-file--${item.name}-${i}`} className="text-black">
                {item?.name} {item?.refNo?.length ? `(${item.refNo})` : ''}
                  {i !== certsNoFile.length - 1 && ", "}
              </span>
              )) : ""}
              {(!certificates?.length && !certsNoFile?.length) ? "-" : ""}
            </div>
          ) : <></>}
          {node?.field_product_testing_method?.name?.length ?
            (
              <div
                className="product-teaser--technology relative text-left flex gap-1 items-center">{t?.all?.technology}: {node.field_product_testing_method?.field_testing_method_link?.url?.length ? (
                <Link className="text-primary hover:underline"
                      href={node.field_product_testing_method.field_testing_method_link.url}>
                  {node.field_product_testing_method.name}
                </Link>
              ): <></>}</div>
            ) : (<></>)
          }
          <div className="mb-3">
            <VariationSelect variations={variations} callback={setActiveVariant} hoverEffect={false} className="!font-normal" chevronColor="black" />
          </div>
        </div>
      </div>
      <div className="product-teaser--product-number py-2">
        {activeVariant && <RenderProductNumber variant={activeVariant} showModal={false} singleLine={true} />}
      </div>
      <div className="product-teaser--buttons pt-2 mt-auto">
        <div className={classNames(
          "grid gap-2 md:grid-cols-1 mb-6 text-sm max-md:hidden",
          {
            // @todo do a locale check here in future.
            "xl:grid-cols-2": true,
          },
        )}>
          <NodeProductDocumentButton field={productBrochure} label={`${t?.all?.product_brochure}`} small={true}
                                     buttonClass={"btn-product-brochure"} callback={setButtonOpen} hideSmall={true}/>
          <NodeProductDocumentButton field={productSheet} label={`${t?.all?.product_sheet}`} small={true}
                                     buttonClass={"btn-product-sheet"} callback={setButtonOpen} hideSmall={true}/>
          <NodeProductDocumentButton field={productManual} label={`${t?.all?.product_instructions}`} small={true}
                                     buttonClass={"btn-product-instructions"} callback={setButtonOpen}
                                     hideSmall={true}/>
          <NodeProductDocumentButton field={readyReferenceSheet} label={`${t?.all?.reference_guide_short}`} small={true}
                                     buttonClass={"btn-reference-sheet"} callback={setButtonOpen} hideSmall={true}/>
        </div>
        {node?.path?.alias && (
          <div>
            <Button href={node.path.alias} className="py-1.5 px-6 btn-product-details" hover={{color: "primary"}}
                    invert={true} containerClassName={"!w-full !block"}>{`${t?.all?.see_product_details}`}</Button>
          </div>
        )}
      </div>
    </article>
  );
}

